import { GetServerSideProps } from "next";
import Link from "next/link";

import archetypesJsonData from "../../assets/traitInfo/archetypes.json";
import { usePublicProfileContext } from "@/context/PublicProfileContext";
import MobileSearchPage from "@/components/publicProfiles/Header/MobileSearchPage";
import ProfileHeader from "@/components/publicProfiles/Header/ProfileHeader";
import BreadCrumbs from "@/components/shared/breadCrumbs/BreadCrumbs";
import styles from "../../styles/archetypeGroup/archetypeGroup.module.css";
import Archetype from "@/components/traits/arcehtype/Archetypes/Archetype";
import ArchetypeGroupLeftBar from "@/components/traits/archetypeGroup/ArchetypeGroupLeftBar";
import { ExtendedArchetype } from "@/models/sharedModels";
import TraitsAndDimensionsDownloadButton from "@/components/traits/shared/TraitsDownloadButton/TraitsAndDimensionsDownloadButton";
import Footers from "@/components/shared/Footers/Footers";
import TraitsSEOHeader from "@/components/traits/shared/TraitsSEOHeader";
import LargeTraitsHeading from "@/components/traits/shared/LargeTraitsHeading/LargeTraitsHeading";
import { archetypeMapper } from "@/assets/traitInfo/dimensionData";

type ArchetypeGroupSlugPageProps = {
  archetypes: ExtendedArchetype[];
  slugName: string;
  slug: string;
  description: string;
};

export default function ArchetypeGroupSlugPage(
  props: ArchetypeGroupSlugPageProps
) {
  const { mobileSearchOpen } = usePublicProfileContext();

  return (
    <>
      <TraitsSEOHeader
        title={`Dimensional || ${props.slugName}`}
        description={props.description}
      />
      {mobileSearchOpen === true && <MobileSearchPage />}
      <ProfileHeader />
      <div className={styles.main}>
        <div className={styles.top}>
          <BreadCrumbs
            list={["Archetype Group", props.slugName]}
            urls={["/archetype-group", ""]}
          />
          <LargeTraitsHeading title={props.slugName} />
        </div>
        <div className={styles.parent}>
          <ArchetypeGroupLeftBar description={props.description} />
          <div className={styles.rightBarMain}>
            <h3 className={styles.rightBarTitle}>
              Archetypes in {props.slugName}
            </h3>
            <div className={styles.elementsWrapper}>
              <div className={styles.elementsDiv}>
                {props.archetypes.map((arch, index) => {
                  return (
                    <Link
                      href={`/traits/archetypes/${arch.slug}`}
                      key={arch.slug}
                      className="elBodyNoMarginMobile"
                    >
                      <Archetype archetype={arch} />
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className={styles.buttonDiv}>
              <TraitsAndDimensionsDownloadButton message="Find your score on over 100 traits on Dimensional." />
            </div>
          </div>
        </div>
      </div>
      <Footers />
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  let slug: any;
  let dataSlug: any;
  let archetype: any[] = [];
  let description: any;

  if (ctx?.params?.slug) {
    slug = ctx.params.slug;
  }

  const archetypeDescriptions: any = {
    identity:
      "Identity Archetypes are based on the Jungian model of 16 personality types. Our Identity archetype represents our primary mode of cognition: how we perceive and react to the world, and how we process information and make decisions.",
    "personality-II":
      "Identity Archetypes are based on the Jungian model of 16 personality types. Our Identity archetype represents our primary mode of cognition: how we perceive and react to the world, and how we process information and make decisions.",
    "love-style":
      "Love Archetypes represent how we're wired in the world of romantic love. This archetype explores how we conceptualize love, how we integrate partners into our lives, what we want out of love, and how we rely on partners to fulfill our needs",
    work: "Work Archetypes are based on the DISC model of behavior. This archetype represents our primary mode of behavior in the work place: what motivates us, how we communicate and influence others, and what environments allow us to thrive.",
    career:
      "Career Archetypes represent how our broadly defined interests can determine the types of careers that make us happy. This archetype explores the kinds of activities that make us feel most satisfied and integrated.",
    spirit:
      "Spirit Archetypes are based on the Cosmology of the 4 elements: Earth, Wind, Fire, and Water. Our spirit archetype represents our simple, inner essence. It is the figurative life force that flows within us and orients us on our life's journey.",
    values:
      "Values Archetypes represent our underlying hierarchy of motivations. This archetype explores how our values affect our choices and priorities in life. In this way, our values archetypes represent a sort of compass. The more our life aligns with that compass, the more harmony we feel.",
  };

  if (slug) {
    archetypesJsonData.forEach((arch) => {
      if (arch.dimensionSlug === slug && arch.isDiscoverable === true) {
        archetype.push(arch);
      }
    });
    dataSlug = archetypeMapper[slug];
    description = archetypeDescriptions[slug];
  }

  if (archetype.length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      archetypes: archetype,
      slugName: dataSlug,
      slug: slug,
      description: description,
    },
  };
};
